import { addDays, format, parseISO, startOfWeek, type Day } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Dates cross every boundary as strings (doc 14): entryDate is YYYY-MM-DD,
 * timestamps are ISO. Everything is displayed in the company timezone.
 */

export const DEFAULT_TZ = 'America/Chicago';

/** Today's YYYY-MM-DD in the company timezone. */
export function todayInTz(tz: string, now: Date = new Date()): string {
  return formatInTimeZone(now, tz, 'yyyy-MM-dd');
}

/** Add days to a YYYY-MM-DD string (calendar math, timezone-free). */
export function addDaysOnly(dateOnly: string, days: number): string {
  return format(addDays(parseISO(dateOnly), days), 'yyyy-MM-dd');
}

/** "Thu, Sep 17, 2026" from YYYY-MM-DD. */
export function fmtDateLong(dateOnly: string | null | undefined): string {
  if (!dateOnly) return '—';
  return format(parseISO(dateOnly), 'EEE, MMM d, yyyy');
}

/** "Sep 17" from YYYY-MM-DD. */
export function fmtDateShort(dateOnly: string | null | undefined): string {
  if (!dateOnly) return '—';
  return format(parseISO(dateOnly), 'MMM d');
}

/** "Thu, Sep 17" (weekday, no year). */
export function fmtDateWeekday(dateOnly: string): string {
  return format(parseISO(dateOnly), 'EEE, MMM d');
}

/** "Aug 24, 2026" */
export function fmtDateMedium(dateOnly: string | null | undefined): string {
  if (!dateOnly) return '—';
  return format(parseISO(dateOnly), 'MMM d, yyyy');
}

/** "4:12 PM" from an ISO timestamp, in the company timezone. */
export function fmtTime(iso: string | null | undefined, tz: string): string {
  if (!iso) return '—';
  return formatInTimeZone(parseISO(iso), tz, 'h:mm a');
}

/** "Thu, Sep 17 · 4:12 PM" */
export function fmtDateTime(iso: string | null | undefined, tz: string): string {
  if (!iso) return '—';
  return formatInTimeZone(parseISO(iso), tz, 'EEE, MMM d · h:mm a');
}

/** "Today 7:31 PM" / "Tue 6:10 PM" / "Sep 3 · 4:12 PM" relative to today in tz. */
export function fmtWhen(
  iso: string | null | undefined,
  tz: string,
  now: Date = new Date(),
): string {
  if (!iso) return '—';
  const d = parseISO(iso);
  const day = formatInTimeZone(d, tz, 'yyyy-MM-dd');
  const today = todayInTz(tz, now);
  if (day === today) return `Today ${formatInTimeZone(d, tz, 'h:mm a')}`;
  if (day === addDaysOnly(today, -1)) return `Yesterday ${formatInTimeZone(d, tz, 'h:mm a')}`;
  return formatInTimeZone(d, tz, 'MMM d · h:mm a');
}

/** "7:00 PM" from "19:00". */
export function fmtClock(hhmm: string): string {
  const [h = '0', m = '00'] = hhmm.split(':');
  const hour = Number(h);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${m} ${suffix}`;
}

/** Whether `now` in tz is past the HH:MM cutoff for the given date. */
export function isPastCutoff(
  dateOnly: string,
  cutoff: string,
  tz: string,
  now: Date = new Date(),
): boolean {
  const today = todayInTz(tz, now);
  if (dateOnly < today) return true;
  if (dateOnly > today) return false;
  const nowLocal = formatInTimeZone(now, tz, 'HH:mm');
  return nowLocal >= cutoff;
}

const DAY_INDEX: Record<string, Day> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

/** Start of the week containing dateOnly for the company's weekStartDay. */
export function weekStartFor(dateOnly: string, weekStartDay: string): string {
  const weekStartsOn = DAY_INDEX[weekStartDay] ?? 1;
  return format(startOfWeek(parseISO(dateOnly), { weekStartsOn }), 'yyyy-MM-dd');
}

/** Seven YYYY-MM-DD strings from a week start. */
export function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysOnly(weekStart, i));
}

/** "Mon Sep 14 – Sun Sep 20, 2026" */
export function fmtWeekRange(weekStart: string): string {
  const end = addDaysOnly(weekStart, 6);
  return `${format(parseISO(weekStart), 'EEE MMM d')} – ${format(parseISO(end), 'EEE MMM d, yyyy')}`;
}

/** An ISO timestamp for a local wall-clock time on a date in tz (used by the mock seed). */
export function isoAt(dateOnly: string, hhmm: string, tz: string): string {
  return fromZonedTime(`${dateOnly}T${hhmm}:00`, tz).toISOString();
}

export function nowInTz(tz: string, now: Date = new Date()): Date {
  return toZonedTime(now, tz);
}
